import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList,
  FolderOpen,
  FileText,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  BarChart3
} from "lucide-react";
import { Link } from "react-router-dom";
import ResumoFinanceiroDiaWidget from "@/components/ResumoFinanceiroDiaWidget";

function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  const statusColors = {
    backlog: "bg-slate-100 text-slate-700",
    planned: "bg-blue-100 text-blue-700",
    "in-progress": "bg-amber-100 text-amber-700",
    "waiting-materials": "bg-orange-100 text-orange-700",
    completed: "bg-emerald-100 text-emerald-700"
  };

  const statusLabels = {
    backlog: "Backlog",
    planned: "Planejado",
    "in-progress": "Em Progresso",
    "waiting-materials": "Aguard. Materiais",
    completed: "Concluído"
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Projetos Ativos</p>
                <p className="text-3xl font-bold text-luvcorp-navy">{stats?.total_projects || 0}</p>
              </div>
              <div className="h-12 w-12 bg-luvcorp-navy/10 rounded-lg flex items-center justify-center">
                <FolderOpen className="h-6 w-6 text-luvcorp-navy" />
              </div>
            </div>
            <Link to="/projects" className="flex items-center gap-1 text-sm text-luvcorp-gold mt-4 hover:underline">
              Ver projetos <ArrowUpRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Ordens de Serviço</p>
                <p className="text-3xl font-bold text-luvcorp-navy">{stats?.total_service_orders || 0}</p>
              </div>
              <div className="h-12 w-12 bg-luvcorp-gold/10 rounded-lg flex items-center justify-center">
                <ClipboardList className="h-6 w-6 text-luvcorp-gold" />
              </div>
            </div>
            <Link to="/service-orders" className="flex items-center gap-1 text-sm text-luvcorp-gold mt-4 hover:underline">
              Ver ordens <ArrowUpRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Registros Diários</p>
                <p className="text-3xl font-bold text-luvcorp-navy">{stats?.total_daily_logs || 0}</p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <Link to="/daily-logs" className="flex items-center gap-1 text-sm text-luvcorp-gold mt-4 hover:underline">
              Ver diários <ArrowUpRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Usuários</p>
                <p className="text-3xl font-bold text-luvcorp-navy">{stats?.total_users || 0}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <Link to="/users" className="flex items-center gap-1 text-sm text-luvcorp-gold mt-4 hover:underline">
              Gerenciar <ArrowUpRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Resumo Financeiro do Dia */}
      <ResumoFinanceiroDiaWidget />

      {/* Status Breakdown & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-luvcorp-gold" />
              Status das Ordens de Serviço
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats?.status_breakdown && Object.entries(stats.status_breakdown).length > 0 ? (
              Object.entries(stats.status_breakdown).map(([status, count]) => (
                <div key={status} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge className={statusColors[status] || "bg-slate-100"}>
                      {statusLabels[status] || status}
                    </Badge>
                    <span className="text-sm font-medium text-slate-700">{count}</span>
                  </div>
                  <Progress 
                    value={(count / stats.total_service_orders) * 100} 
                    className="h-2"
                  />
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Nenhuma ordem de serviço cadastrada
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Clock className="h-5 w-5 text-luvcorp-gold" />
              Atividade Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_activity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0">
                    <div className="h-8 w-8 bg-luvcorp-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-luvcorp-navy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {activity.description?.slice(0, 50)}...
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {activity.created_by_name || "Sistema"} • {new Date(activity.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge variant={activity.status === "approved" ? "default" : "secondary"} className="text-xs">
                      {activity.status === "approved" ? "Aprovado" : "Pendente"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Nenhuma atividade recente
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-heading">Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/service-orders/new">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:border-luvcorp-gold hover:text-luvcorp-gold">
                <ClipboardList className="h-6 w-6" />
                <span className="text-sm">Nova OS</span>
              </Button>
            </Link>
            <Link to="/projects/new">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:border-luvcorp-gold hover:text-luvcorp-gold">
                <FolderOpen className="h-6 w-6" />
                <span className="text-sm">Novo Projeto</span>
              </Button>
            </Link>
            <Link to="/daily-logs/new">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:border-luvcorp-gold hover:text-luvcorp-gold">
                <FileText className="h-6 w-6" />
                <span className="text-sm">Novo RDO</span>
              </Button>
            </Link>
            <Link to="/kanban">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:border-luvcorp-gold hover:text-luvcorp-gold">
                <TrendingUp className="h-6 w-6" />
                <span className="text-sm">Ver Kanban</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<DashboardHome />} />
      </Routes>
    </MainLayout>
  );
}
